/**
 * Cars with a job in them (research R8; GAMEPLAY-ANALYSIS WP-X7): six particular parked motors about the
 * borough, each with a one-off mini-story. Get in, and a note on the dash or a voice from the back seat
 * offers the job; stay in for a few seconds to take it, get out to say no. Found by exploring and stealing
 * — nothing on the job board, a small "?" on the minimap only once you are close — and kept in the book.
 *
 * Every car job is a plain `MissionDef` (id `car-job-<id>`, one-off: the save's completed list keeps it)
 * that `carJobSystem.ts` hands to the mission system at the moment the offer is taken, the way the
 * half-bricks do. Its first act is to adopt the car you are sitting in as the job's 'car'. Pay £200–350
 * base plus one bonus line: about £1.9k for all six, once — in line with a street job each.
 */
import type { MissionScript } from '../missions/runner';
import { UNLOCK_STREET_JOBS } from '../missions/ids';
import { holdHint } from '../missions/shared';
import type { MissionDef, RewardLine, StageDef } from '../missions/types';
import type { V2 } from '../core/math';
import type { Vehicle } from '../vehicles/types';

export const CAR_JOB_PREFIX = 'car-job-';
/** The mission key of the car itself. */
export const CAR_KEY = 'car';
/** Tag on a spawned car-job motor: `carjob:<id>`. */
export const CAR_JOB_TAG = 'carjob';

export interface CarJob {
  id: string;
  title: string;
  /** The POI the car is parked by (at the nearest kerb spot). */
  near: string;
  /** Where it is, in words (the book). */
  place: string;
  spec: string;
  paint?: number;
  /** Who speaks: a voice from the back seat, or the note on the dash. */
  voice: string;
  /** Said when you get in (the offer). Spoken line: period voice allowed. */
  offer: string;
  /** Said if you get out again (the refusal). */
  refuse: string;
  /** What the book says once done (plain). */
  done: string;
  /** Advertised pay (the intro card). */
  reward: number;
  build(job: CarJob): Pick<MissionDef, 'blurb' | 'intro' | 'outro' | 'stages' | 'payout'> & Partial<Pick<MissionDef, 'protect' | 'outroFocus' | 'mood'>>;
}

const num = (m: MissionScript, k: string): number => (typeof m.vars[k] === 'number' ? (m.vars[k] as number) : 0);
/** Condition of the job's car, 0..1. */
const cond = (m: MissionScript): number => {
  const v = m.vehicle(CAR_KEY);
  return v ? v.health / v.spec.maxHealth : 0;
};
/** In the job's car, stopped (< `speed` m/s) within `r` m of `at`. */
function stoppedAt(m: MissionScript, at: V2, r: number, speed = 1.5): boolean {
  const player = m.ctx.services.get('player');
  const v = m.vehicle(CAR_KEY);
  return !!v && player.vehicle === v && v.speed < speed && Math.hypot(v.pos.x - at.x, v.pos.z - at.z) <= r;
}

/** Toni's round: seconds parked at each stop, the clock, and the tip. */
export const ICES_WAIT = 3;
export const ICES_TIME = 150;
export const ICES_TIP_LEFT = 50;
/** The man with the ship. */
export const TIDE_TIME = 90;
export const TIDE_SPARE = 25;
export const WEDDING_TIME = 150;

export const CAR_JOBS: readonly CarJob[] = [
  {
    id: 'wreaths',
    title: 'Flowers for the Wake',
    near: 'st-brannocs',
    place: 'the undertaker’s black saloon outside St. Brannoc’s',
    spec: 'saloon',
    paint: 0x141416,
    voice: 'A note on the dash',
    offer: '“Wreaths for the Abbott wake — Brickmakers Arms, Kiln Street. Gently, they bruise. £200 at the bar.” — Coyle & Sons',
    refuse: 'The note flutters back onto the dash.',
    done: 'Drove the Abbott wreaths to the Brickmakers Arms.',
    reward: 200,
    build: () => ({
      blurb: 'The undertaker’s car is full of wreaths for a wake. Drive them to the Brickmakers Arms on Kiln Street without crushing them. Pays £200, more if not a petal is out of place.',
      intro: [
        { who: 'A note on the dash', text: 'Coyle & Sons regret they are short of a driver. Kindly deliver the floral tributes. Gently.' },
      ],
      protect: [{ vehicle: CAR_KEY, minHealth: 0.55, reason: 'The wreaths are flattened — nobody wants those at a wake. Try again and drive gently.' }],
      stages: [
        {
          id: 'drive',
          kind: 'goTo',
          at: { poi: 'pub-brickmakers' },
          radius: 10,
          vehicle: CAR_KEY,
          text: 'Drive the wreaths to the Brickmakers Arms on Kiln Street.',
          hint: 'Drive gently: every knock crushes the flowers, and the bonus goes with them.',
          onComplete: (m) => {
            m.vars.cond = cond(m);
          },
        },
      ],
      outroFocus: { poi: 'pub-brickmakers' },
      outro: [{ who: 'The landlord', text: 'Lovely. Old Abbott would have liked those. Your money’s behind the bar.' }],
      payout: (m) => {
        const lines: RewardLine[] = [{ label: 'Wreaths delivered', amount: 200 }];
        if (num(m, 'cond') >= 0.95) lines.push({ label: 'Not a petal out of place', amount: 100 });
        return lines;
      },
    }),
  },
  {
    id: 'ices',
    title: 'Toni’s Round',
    near: 'kiln-common',
    place: 'the ice-cream van by Kiln Common',
    spec: 'van',
    paint: 0xf0e2c0,
    voice: 'Toni, in the back',
    offer: 'Ah, thank God — somebody. My back’s gone, I can’t stand up. Do my round for me? Three stops, half the takings.',
    refuse: 'No? I’ll just lie here, then. On the wafers.',
    done: 'Did Toni’s ice-cream round while he lay in the back.',
    reward: 180,
    build: () => {
      const stops: { poi: string; name: string }[] = [
        { poi: 'pub-brickmakers', name: 'the Brickmakers Arms' },
        { poi: 'printworks-moss-yard', name: 'Moss’s Yard' },
        { poi: 'safehouse', name: 'Pilcher Street' },
      ];
      const stages: StageDef[] = stops.map((s, i) => ({
        id: `stop-${i + 1}`,
        kind: 'custom',
        at: { poi: s.poi },
        radius: 10,
        text: `Stop ${i + 1} of 3: park the van at ${s.name} and wait for the queue.`,
        hint: 'Stop by the marker and stay put until the bar is full.',
        done: (m) => num(m, `wait${i}`) >= ICES_WAIT,
        progress: (m) => num(m, `wait${i}`) / ICES_WAIT,
        onEnter: i === 0 ? (m) => m.startTimer(ICES_TIME, 'The ice cream melted before the round was done. Try again and keep moving between stops.') : undefined,
        onTick: (m, dt) => {
          if (stoppedAt(m, m.resolve({ poi: s.poi }), 12)) m.vars[`wait${i}`] = num(m, `wait${i}`) + dt;
        },
        onComplete: (m) => m.say([{ who: 'A child', text: i === 2 ? 'Two ninety-nines and a lolly, mister!' : 'A choc ice, please!', seconds: 2 }]),
      }));
      return {
        blurb: `Toni’s back has gone and he can’t drive his ice-cream van. Do his round: three stops before the ice cream melts (${ICES_TIME / 60 | 0} minutes ${ICES_TIME % 60} seconds). Half the takings are yours.`,
        intro: [{ who: 'Toni, in the back', text: 'Brickmakers, Moss’s Yard, Pilcher Street. The chimes are the button by your knee.' }],
        stages,
        outro: [{ who: 'Toni, in the back', text: 'You’re a saint. A saint with a van. Here — half, like I said.' }],
        payout: (m) => {
          const lines: RewardLine[] = [{ label: 'Half the takings (3 stops)', amount: 180 }];
          if (num(m, 'timeLeftAtEnd') >= ICES_TIP_LEFT) lines.push({ label: 'Toni’s tip: nothing melted', amount: 60 });
          return lines;
        },
      };
    },
  },
  {
    id: 'tide',
    title: 'The Tide Waits',
    near: 'club-velvet-lamp',
    place: 'the minicab with its meter running outside the Velvet Lamp',
    spec: 'cab',
    voice: 'A man in the back',
    offer: 'Meter’s running, driver. Basin Crane Quay, and I sail at the turn of the tide. Ninety seconds. Go.',
    refuse: 'Fine. I’ll swim.',
    done: 'Got a man to the Basin Crane Quay before his ship sailed.',
    reward: 250,
    build: () => ({
      blurb: 'A passenger in the back of a minicab has to catch his ship. Drive him to the Basin Crane Quay within 90 seconds. Pays £250, more with time to spare.',
      intro: [{ who: 'A man in the back', text: 'Don’t ask what’s in the case. Just drive.' }],
      stages: [
        {
          id: 'quay',
          kind: 'goTo',
          at: { poi: 'docks-crane-quay' },
          radius: 12,
          vehicle: CAR_KEY,
          text: 'Drive your passenger to the Basin Crane Quay before his ship sails.',
          hint: 'Ninety seconds. The quickest way is down to Dock Road and along the water.',
          onEnter: (m) => m.startTimer(TIDE_TIME, 'Too late: the ship sailed without him. Try again and take the quickest way to the docks.'),
        },
      ],
      outroFocus: { poi: 'docks-crane-quay' },
      outro: [{ who: 'A man in the back', text: 'Made it. Keep the meter, and this. You never saw me.' }],
      payout: (m) => {
        const lines: RewardLine[] = [{ label: 'The fare, and then some', amount: 250 }];
        if (num(m, 'timeLeftAtEnd') >= TIDE_SPARE) lines.push({ label: 'Time to spare', amount: 100 });
        return lines;
      },
    }),
  },
  {
    id: 'flit',
    title: 'Moonlight Flit',
    near: 'phone-tanners',
    place: 'the removal lorry on Bell Street',
    spec: 'lorry',
    paint: 0x5a4a36,
    voice: 'A note on the wheel',
    offer: '“Everything we own is in the back. Get it to the Kiln Road Goods Depot before the landlord’s men see. £300 in the glovebox.” — The Dwyers',
    refuse: 'You put the note back on the wheel.',
    done: 'Moved the Dwyers’ furniture out from under their landlord.',
    reward: 300,
    build: () => ({
      blurb: 'The Dwyers are doing a moonlight flit from their landlord. Drive their removal lorry to the Kiln Road Goods Depot, with no police following you. Pays £300.',
      intro: [{ who: 'A note on the wheel', text: '“The depot gate will be open. Don’t bring the police — they’ll tell the landlord.”' }],
      stages: [
        {
          id: 'depot',
          kind: 'goTo',
          at: { poi: 'kiln-goods-depot' },
          radius: 12,
          vehicle: CAR_KEY,
          requireHeat0: true,
          text: 'Drive the lorry to the Kiln Road Goods Depot.',
          hint: 'It’s a big lorry: take the wide roads and brake early.',
          blockedText: 'The police are following you. Lose them before you go in.',
        },
      ],
      outroFocus: { poi: 'kiln-goods-depot' },
      outro: [{ who: 'Mr Dwyer', text: 'All of it, and not a chair broke. The landlord can whistle for it.' }],
      payout: () => [{ label: 'From the glovebox', amount: 300 }],
    }),
  },
  {
    id: 'keys',
    title: 'Keys in It',
    near: 'garage-kiln-motors',
    place: 'the red coupe with the keys in it on Kiln Road',
    spec: 'coupe',
    paint: 0xb23a2a,
    voice: 'A note on the dash',
    offer: '“Keys in it. Leave it in Lockup 3, Coal Drop Lane. £300 under the mat. Ask no questions.”',
    refuse: 'Too good to be true. You leave the note where it was.',
    done: 'Drove off in a police bait car — and sold its radio.',
    reward: 300,
    build: () => ({
      blurb: 'A coupe with the keys in it and a note: drive it to Lockup 3 on Coal Drop Lane for £300. Too good to be true? Pays £300.',
      intro: [{ who: 'A note on the dash', text: '“No questions. The shutter will be up.”' }],
      stages: [
        {
          id: 'drive',
          kind: 'custom',
          at: { poi: 'lockup-coal-drop' },
          radius: 10,
          text: 'Drive the coupe to Lockup 3 on Coal Drop Lane.',
          // The twist comes once you've driven off with it (a few seconds, a few car lengths).
          onEnter: (m) => {
            const v = m.vehicle(CAR_KEY);
            m.vars.x0 = v?.pos.x ?? 0;
            m.vars.z0 = v?.pos.z ?? 0;
          },
          done: (m) => {
            const v = m.vehicle(CAR_KEY);
            return m.stageTime > 4 && !!v && Math.hypot(v.pos.x - num(m, 'x0'), v.pos.z - num(m, 'z0')) > 15;
          },
          onComplete: (m) => {
            m.say([{ who: 'Police radio, under the seat', text: 'Bait car is moving, Kiln Road. All units, all units!' }]);
            m.toast('It’s a police bait car!', 'warn');
            m.setHeat(2);
          },
        },
        { id: 'lose', kind: 'escapeHeat', text: 'It’s a police bait car. Lose the police.', hint: 'Get out of their sight and stay hidden until your wanted level clears.' },
        {
          id: 'radio',
          kind: 'hold',
          at: { vehicle: CAR_KEY },
          radius: 4,
          seconds: 2,
          prompt: 'Rip out the radio',
          text: 'Pull the police radio out from under the seat.',
          hint: holdHint('Get out and stand by the driver’s door.'),
        },
        {
          id: 'stash',
          kind: 'goTo',
          at: { poi: 'lockup-coal-drop' },
          radius: 10,
          vehicle: CAR_KEY,
          requireHeat0: true,
          text: 'Now leave the coupe in Lockup 3 on Coal Drop Lane.',
          blockedText: 'The police are after you again. Lose them first.',
        },
      ],
      outroFocus: { poi: 'lockup-coal-drop' },
      outro: [{ who: 'A voice in the lockup', text: 'A bait car? Then it’s worth double. A police radio fetches a fortune round here.' }],
      payout: () => [
        { label: 'Under the mat', amount: 300 },
        { label: 'The police radio', amount: 100 },
      ],
    }),
  },
  {
    id: 'wedding',
    title: 'Here Comes the Bride',
    near: 'pub-crooked-anchor',
    place: 'the white wedding car outside the Crooked Anchor',
    spec: 'saloon',
    paint: 0xf2efe6,
    voice: 'The bride’s father, in the back',
    offer: 'You’re not Reg. Reg is in the Anchor, legless. Never mind — you’ll do. The bride’s at Lamb Walk Market and we’re late!',
    refuse: 'Oh, marvellous. REG!',
    done: 'Got the bride to St. Brannoc’s on time.',
    reward: 250,
    build: () => ({
      blurb: 'The wedding driver is drunk. Collect the bride at Lamb Walk Market and get her to St. Brannoc’s Church within 2 minutes 30, without a scratch on the car. Pays £250, more if the car is spotless.',
      intro: [{ who: 'The bride’s father, in the back', text: 'Her mother’s stall, Lamb Walk. Then the church. And mind the paint!' }],
      protect: [{ vehicle: CAR_KEY, minHealth: 0.55, reason: 'The wedding car is a wreck — the bride won’t arrive in that. Try again and drive carefully.' }],
      stages: [
        {
          id: 'bride',
          kind: 'custom',
          at: { poi: 'shop-lamb-walk' },
          radius: 10,
          text: 'Collect the bride at Lamb Walk Market.',
          hint: 'Stop by the marker. She’ll get in.',
          done: (m) => stoppedAt(m, m.resolve({ poi: 'shop-lamb-walk' }), 12),
          onEnter: (m) => m.startTimer(WEDDING_TIME, 'Too late: the vicar gave up and went home. Try again and take the quickest way.'),
          onComplete: (m) => m.say([{ who: 'The bride', text: 'Dad! You said the Grandee! …Oh, never mind. Drive!' }]),
        },
        {
          id: 'church',
          kind: 'goTo',
          at: { poi: 'st-brannocs' },
          radius: 10,
          vehicle: CAR_KEY,
          text: 'Drive the bride to St. Brannoc’s Church.',
          hint: 'Quick, but every knock shows on a white car.',
          onComplete: (m) => {
            m.vars.cond = cond(m);
          },
        },
      ],
      outroFocus: { poi: 'st-brannocs' },
      outro: [{ who: 'The bride’s father', text: 'On time, near enough. Here — and come to the do after. Not you, Reg.' }],
      payout: (m) => {
        const lines: RewardLine[] = [{ label: 'The bride delivered', amount: 250 }];
        if (num(m, 'cond') >= 0.9) lines.push({ label: 'Not a mark on the car', amount: 100 });
        return lines;
      },
    }),
  },
];

/** Seconds you have to stay in the car once the offer is made (get out sooner and it's a no). */
export const CAR_JOB_OFFER_WINDOW = 4;

export const carJobId = (job: CarJob): string => `${CAR_JOB_PREFIX}${job.id}`;

/**
 * The job as a `MissionDef`. `findCar` returns the motor the job started from (the one you're sitting in);
 * on "start again" after it was lost, `spawnAt` is where a fresh one is put, and you're put in it.
 */
export function carJobDef(job: CarJob, findCar: () => Vehicle | null, spawnAt: () => V2): MissionDef {
  const b = job.build(job);
  return {
    id: carJobId(job),
    title: job.title,
    client: job.voice,
    tag: 'A CAR WITH A JOB IN IT',
    blurb: b.blurb,
    reward: job.reward,
    requires: [],
    requiresUnlock: UNLOCK_STREET_JOBS,
    giver: { poi: job.near, prompt: job.title, glyph: '?' },
    intro: b.intro,
    outro: b.outro,
    ...(b.outroFocus ? { outroFocus: b.outroFocus } : {}),
    ...(b.protect ? { protect: b.protect } : {}),
    abandon: { vehicle: CAR_KEY, distance: 70, reason: `You walked away from the job and left the ${job.spec === 'lorry' ? 'lorry' : job.spec === 'van' ? 'van' : 'car'} behind. Get back in it next time.`, label: `the ${job.spec === 'lorry' ? 'lorry' : job.spec === 'van' ? 'van' : 'car'}` },
    setup: (m) => {
      const v = findCar();
      if (v && !v.destroyed) {
        m.adoptVehicle(CAR_KEY, v);
        if (m.ctx.services.get('player').vehicle !== v) m.placePlayer({ vehicle: CAR_KEY }, CAR_KEY);
        return;
      }
      m.spawnVehicle(CAR_KEY, job.spec, { pos: spawnAt() }, job.paint !== undefined ? { paint: job.paint } : {});
      m.placePlayer({ vehicle: CAR_KEY }, CAR_KEY);
    },
    stages: b.stages,
    payout: b.payout,
    resultStamp: 'ODD JOB DONE',
    resultLine: () => `${job.done} Found in ${job.place}.`,
  };
}
